/**
 * InputText
 *
 * Reusable text input primitive for the entire web project. Renders a
 * `<textarea>` by default and switches to `<input type="text">` when
 * `multiline === false`. Replaces every direct `<textarea>` and (post user-
 * review fix plan finding 4) every direct `<input type="text">` in
 * `expause_web/src/`. Callers configure sizing/visuals via props; defaults are
 * theme/responsive-driven so a bare `<InputText value=... onChange=... />`
 * renders consistent with the rest of the app.
 *
 * Mirrors Flutter's `TextField` surface used in
 * `lib/presentation/web/contact_screen.dart:340-358` (expands + maxLines: null
 * + LengthLimitingTextInputFormatter(1000) + hintText + cursorColor) — but
 * reusable across every web textarea/single-line call-site.
 *
 * When `multiline === false`:
 *   - Renders `<input type="text">` instead of `<textarea>`.
 *   - The `rows`, `autoResize`, `maxHeight`, and `resize` props are silently
 *     ignored (they only apply to the textarea branch).
 *   - The `inputMode` prop is forwarded to the `<input>` so callers can opt
 *     into numeric/decimal keyboards on mobile.
 */

import React, { useEffect, useId } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { qaAttr, type QaAttrOptions } from '@/presentation/utils/qaAttr';

/**
 * Union of the two underlying DOM element types `InputText` can render.
 * Exported so callers that hold an `inputRef` declare a ref compatible with
 * both single-line and multiline modes without having to re-derive the union.
 */
export type InputTextElement = HTMLTextAreaElement | HTMLInputElement;

export interface InputTextProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  maxLength?: number;
  /** Initial visible rows. Default 1 (matches the existing MessageInput textarea). Ignored when `multiline === false`. */
  rows?: number;
  /**
   * When `false`, renders `<input type="text">` instead of `<textarea>`.
   * Defaults to `true` to match the existing textarea call-sites.
   */
  multiline?: boolean;
  /**
   * Auto-grow the textarea as the user types (up to `maxHeight`).
   * Only `MessageInput` and `CommentInput` opt in today. Ignored when
   * `multiline === false`.
   */
  autoResize?: boolean;
  disabled?: boolean;
  inputRef?: React.MutableRefObject<InputTextElement | null>;
  onKeyDown?: (e: React.KeyboardEvent<InputTextElement>) => void;
  onFocus?: (e: React.FocusEvent<InputTextElement>) => void;
  onBlur?: (e: React.FocusEvent<InputTextElement>) => void;
  /**
   * Single-line only: forwarded as the native `inputMode` attribute on the
   * underlying `<input>`. Useful for opening numeric/decimal keyboards on
   * mobile. Ignored when `multiline === true` (textareas do not honor it).
   */
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  // Visual overrides (defaults pulled from theme/responsive)
  backgroundColor?: string;
  textColor?: string;
  placeholderColor?: string;
  fontSize?: string;
  lineHeight?: number | string;
  /** Ignored when `multiline === false`. */
  maxHeight?: number | string;
  height?: number | string;
  padding?: number | string;
  borderRadius?: number | string;
  /** Ignored when `multiline === false`. */
  resize?: React.CSSProperties['resize'];
  className?: string;
  style?: React.CSSProperties;
  /**
   * Optional `data-qa-*` set forwarded onto the rendered `<input>`/`<textarea>`
   * so the qa-tester can locate the field in both single-line and multiline modes.
   */
  qa?: QaAttrOptions;
}

export const InputText: React.FC<InputTextProps> = ({
  value,
  onChange,
  placeholder,
  maxLength,
  rows,
  multiline = true,
  autoResize = false,
  disabled = false,
  inputRef,
  onKeyDown,
  onFocus,
  onBlur,
  inputMode,
  backgroundColor,
  textColor,
  placeholderColor,
  fontSize,
  lineHeight,
  maxHeight,
  height,
  padding,
  borderRadius,
  resize = 'none',
  className,
  style,
  qa,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const reactId = useId();
  const qaProps = qa ? qaAttr(qa) : {};
  // CSS class names cannot start with a colon (React's useId returns ":r1:" style ids).
  const placeholderClassName = `expause-input-text-${reactId.replace(/:/g, '')}`;

  // Default placeholder color follows the existing MessageInput pattern: when
  // disabled, use theme.disabled; otherwise theme.text.primary.
  const effectivePlaceholderColor =
    placeholderColor ?? (disabled ? theme.disabled : theme.text.primary);

  const effectiveBackground = backgroundColor ?? theme.modalBackground;
  const effectiveTextColor = textColor ?? theme.text.primary;
  const effectiveFontSize = fontSize ?? responsive.font.base;
  const effectiveRows = multiline ? rows ?? 1 : 1;

  const resizeTextarea = (el: HTMLTextAreaElement) => {
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  // When `value` is reset externally (e.g., CommentInput clears after send),
  // keep the auto-resize geometry in sync.
  useEffect(() => {
    if (!autoResize || !multiline) return;
    const el = inputRef?.current;
    // Only the textarea branch resizes; in single-line mode there's nothing to do.
    if (el && el instanceof HTMLTextAreaElement) resizeTextarea(el);
    // We intentionally re-run whenever `value` changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, autoResize, multiline]);

  const sharedStyle: React.CSSProperties = {
    backgroundColor: effectiveBackground,
    color: effectiveTextColor,
    fontSize: effectiveFontSize,
    outline: 'none',
    border: 'none',
    fontFamily: 'inherit',
    ...(lineHeight !== undefined && { lineHeight }),
    ...(height !== undefined && { height }),
    ...(padding !== undefined && { padding }),
    ...(borderRadius !== undefined && { borderRadius }),
    ...style,
  };

  const placeholderStyleBlock = (
    <style>
      {`
          .${placeholderClassName}::placeholder {
            color: ${effectivePlaceholderColor};
          }
        `}
    </style>
  );

  const mergedClassName = [placeholderClassName, className].filter(Boolean).join(' ');

  if (!multiline) {
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    };

    return (
      <>
        {placeholderStyleBlock}
        <input
          {...qaProps}
          ref={inputRef as React.MutableRefObject<HTMLInputElement | null> | undefined}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
          inputMode={inputMode}
          className={mergedClassName}
          style={sharedStyle}
        />
      </>
    );
  }

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
    if (autoResize) {
      resizeTextarea(e.target);
    }
  };

  return (
    <>
      {placeholderStyleBlock}
      <textarea
        {...qaProps}
        ref={inputRef as React.MutableRefObject<HTMLTextAreaElement | null> | undefined}
        rows={effectiveRows}
        value={value}
        onChange={handleTextareaChange}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        className={mergedClassName}
        style={{
          ...sharedStyle,
          resize,
          overflow: autoResize ? 'hidden' : 'auto',
          ...(maxHeight !== undefined && { maxHeight }),
        }}
      />
    </>
  );
};
